/**
 * NY TIDRAPPORT — skärmen produkten satsar på.
 *
 * MÅLET ÄR ÅTTA TIMMAR PÅ TRE SEKUNDER: tryck "8 h", tryck "Spara". Jobbet,
 * momentet och dagen är redan ifyllda med det du senast rapporterade på, och de
 * ligger som DÄMPADE RADER med "Byt" i stället för som fält — den som ska byta
 * hittar dem, den som inte ska det ser dem knappt. Timmarna skrivs som en
 * människa tänker dem: "7,5" eller "7:30" (se tidrapport/timmar.ts).
 *
 * ETT MOMENT = EN TIDRAD. TimeEntryWrite bär ETT task_id och ETT minutes, så
 * "tre och en halv timme, bytte utkastare och provtryckte" är TVÅ rader i samma
 * kuvert. Användaren skriver totalen EN gång och kryssar i vad han gjorde;
 * fördelningen syns per moment INNAN han sparar, och summan blir exakt totalen.
 *
 * MOMENTEN HAMNAR ORDAGRANT PÅ KUNDENS FAKTURA. Det är därför de finns, och
 * därför KRAVET LIGGER PÅ MOMENTLISTAN och aldrig på ett fritextfält — se
 * momentkravFor i tidrapport/regler.ts, som bär appbokens skäl ordagrant.
 * ANTECKNINGEN ÄR FRIVILLIG. Tvingas den fram skriver folk 'jobbat' eller 'wc',
 * och då är den sämre än ingen text alls.
 *
 * PRISET SKICKAS ALDRIG HÄRIFRÅN (beslut 15). TimeEntryWrite har inga prisfält
 * alls; servern slår upp satsen i prislistan och FRYSER den på raden. Saknas
 * priset får raden PRICE_MISSING och sparas inte — och den texten står i
 * shared/errors.ts, inte här.
 *
 * MEN FRÅGAN OM PRISET FINNS STÄLLS INNAN MAN SKRIVER, inte efteråt. Tidigare
 * var ett saknat timpris den enda förutsättningen som INTE prövades i
 * klienten: jobbet, timmarna och momenten hade sina egna besked, men priset
 * upptäcktes först som PRICE_MISSING från servern — med hela formuläret
 * ifyllt, Spara redan tryckt, och ett fel montören inte kan rätta själv.
 * Nu kommer beskedet över knappen med en väg dit priset sätts.
 *
 * DET ÄR INGET PRIS SOM LÄSES. `harMittTimpris` i oversikt.ts är ett ja/nej om
 * MIN roll har en rad i firmans standardlista, räknat där prisdatan ändå går
 * ut, och `null` i montörs- och gästvyn som aldrig får se price_rate (beslut
 * 16). Ögonblicksbilden här bär fortfarande inga prisfält alls — det är därför
 * svaret hämtas ur översikten och inte dras in i tidrapportens lager.
 *
 * RÖSTEN (beslut 02): "DU PRATAR. APPEN SKRIVER." Håll in knappen, säg "åtta
 * timmar på Skolvägen, grävde dagvatten och lastade container, fyra och en halv
 * mil, körde servicebilen", släpp. Servern svarar med ett UTKAST
 * (VoiceTranscribeResponse), och det utkastet FYLLS I I EXAKT DET HÄR
 * FORMULÄRET — samma fält, samma Spara-knapp som vid manuell inmatning. Det som
 * tolkningen var osäker på är markerat så att man tittar extra där; hittade den
 * ingen arbetsorder öppnas orderväljaren i stället för att den gissar. INGENTING
 * SPARAS FÖRRÄN HANTVERKAREN TRYCKT SPARA — det lovar säljsidan uttryckligen, och
 * raden får då source = 'voice'. Inspelningen och anropet ligger i
 * tidrapport/rost.ts; reglerna för utkastet i worker/src/modules/voice/.
 */

import { useMemo, useState } from 'react';
import { asMeters, asMinutes } from '@delat/types';
import type {
  EntrySource,
  SyncOp,
  TaskRow,
  Vehicle,
  VoiceDraftField,
  VoiceTranscribeResponse,
  WorkDate,
} from '@delat/types';
import {
  Bricka,
  FAKTOR,
  Falt,
  Felruta,
  Ikonrad,
  Installningsrad,
  Knapp,
  Kravbricka,
  Kryssrad,
  Laddar,
  Rostknapp,
  Rubrik,
  Snabbrad,
  Talfalt,
  Textyta,
  Tomtext,
  Valfalt,
  Yta,
  franHeltal,
  tillHeltal,
} from '../ui';
import { idag, visaDag, visaMil, visaMinuter } from '../app/datum';
import { useNavigering } from '../app/navigering';
import { MAX_METER_PER_MILRAD } from '../api/konstanter';
import { useInloggad } from '../app/session';
import { FORDON } from '../api/granskare/tidrapport';
import { useOversikt } from './oversikt';
import { useSynk } from './tidrapport/lager';
import type { Ogonblicksbild, Synk } from './tidrapport/lager';
import { useOnline } from './tidrapport/natet';
import { ROSTTEXT, rostfeltext, useRostinspelning } from './tidrapport/rost';
import { felAvOperation, useSparning } from './tidrapport/spara';
import { milradOp, nyttMilradId, nyttTidradId, tidradOp } from './tidrapport/skriv';
import {
  ANTECKNING_MAX_TECKEN,
  FORDONSTEXT,
  VECKOLAGETEXT,
  arbetsdagUrFalt,
  farRapportera,
  farSkrivaVecka,
  inomArbetsdagsfonstret,
  momentkravFor,
  orderMedId,
  ordernamn,
  orderunder,
  senastArbetsdag,
  sorteradeOrdrar,
  tidigastArbetsdag,
  veckanFor,
  veckansLage,
} from './tidrapport/regler';
import {
  MAX_MINUTER,
  fordelaMinuter,
  rimligaMinuter,
  timmarTillMinuter,
  timtextAv,
} from './tidrapport/timmar';

/** CHECK (meters BETWEEN 1 AND 2000000) i schema/company.sql — 200 mil per rad. */
const MAX_METER = MAX_METER_PER_MILRAD;

export const Tidny = () => {
  const { firmaId } = useInloggad();
  const synk = useSynk(firmaId);

  if (synk.bild === null) {
    return synk.fel === null ? (
      <Laddar rader={5} />
    ) : (
      <Felruta
        fel={synk.fel}
        atgard={
          <Knapp variant="piller-mini" onClick={() => void synk.synka(true)}>
            Försök igen
          </Knapp>
        }
      />
    );
  }
  return <Nytidsform bild={synk.bild} synk={synk} />;
};

const Nytidsform = ({
  bild,
  synk,
}: {
  readonly bild: Ogonblicksbild;
  readonly synk: Synk;
}) => {
  const { konto, roll, arChef, arLast, kanSeLaget, firmaId } = useInloggad();
  const { ga } = useNavigering();
  const sparning = useSparning(synk.skicka);
  const online = useOnline();

  const minaRader = useMemo(
    () => bild.tidrader.filter((rad) => rad.account_id === konto.id),
    [bild.tidrader, konto.id],
  );

  /** Senast skrivna raden — id:t är en ULID, alltså sorterbart i tidsordning. */
  const senaste = useMemo(
    () => [...minaRader].sort((a, b) => a.id.localeCompare(b.id)).at(-1) ?? null,
    [minaRader],
  );

  const ordrar = useMemo(() => sorteradeOrdrar(bild), [bild]);

  /**
   * Momenten i den ordning DEN HÄR personen faktiskt använder dem, vanligast
   * först. Prototypen har sju moment; en firma kan ha trettio, och då är det
   * skillnaden mellan ett tryck och en skrollning.
   */
  const moment = useMemo(() => {
    const antal = new Map<string, number>();
    for (const rad of minaRader) {
      antal.set(rad.task_id, (antal.get(rad.task_id) ?? 0) + 1);
    }
    return [...bild.moment].sort((a, b) => {
      const skillnad = (antal.get(b.id) ?? 0) - (antal.get(a.id) ?? 0);
      return skillnad !== 0 ? skillnad : a.name.localeCompare(b.name, 'sv');
    });
  }, [bild.moment, minaRader]);

  const [orderId, sattOrderId] = useState<string | null>(
    () => senaste?.work_order_id ?? ordrar[0]?.id ?? null,
  );
  const [dag, sattDag] = useState<WorkDate>(() => idag());
  const [timtext, sattTimtext] = useState('');
  /**
   * FÖRVALET ÄR DET DU SENAST RAPPORTERADE PÅ, OCH INGENTING ANNARS.
   *
   * Tidigare föll det tillbaka på `moment[0]` när personen saknade historik.
   * Sorteringen ovan är på ANVÄNDNING, och en person utan rader har noll på
   * varje moment — då blir moment[0] det alfabetiskt första. I en ny firma är
   * det ofta "Resa och materialhämtning": den allra första tidrapporten skrev
   * 8 h, användaren kryssade i sitt riktiga moment, och timmarna delades tyst
   * 4 + 4 mellan resa och arbete. Resa ger dessutom ingen ROT, så en halv
   * arbetsdag försvann ur ROT-underlaget utan att någon sa något.
   *
   * Utan historik står listan därför tom, Spara är av och skärmen säger
   * "Kryssa i minst ett moment — det är texten kunden får se". Ett tryck extra
   * på den FÖRSTA rapporten, mot en tyst felbokning på varenda ny firma.
   */
  const [valda, sattValda] = useState<readonly string[]>(() =>
    senaste === null ? [] : [senaste.task_id],
  );
  const [anteckning, sattAnteckning] = useState('');
  const [kalla, sattKalla] = useState<EntrySource>('manual');
  const [visaOrderval, sattVisaOrderval] = useState(false);
  const [visaDagval, sattVisaDagval] = useState(false);
  const [milOppet, sattMilOppet] = useState(false);
  const [miltext, sattMiltext] = useState('');
  const [fordon, sattFordon] = useState<Vehicle>('own_car');
  /**
   * RÖSTENS SPÅR I FORMULÄRET: vilka fält tolkningen var osäker på (markeras
   * tills användaren rört just det fältet) och det senaste svaret, för
   * transkriptet och "hittade ingen order"-beskedet.
   */
  const [osakra, sattOsakra] = useState<ReadonlySet<VoiceDraftField>>(() => new Set());
  const [rostsvar, sattRostsvar] = useState<VoiceTranscribeResponse | null>(null);

  /**
   * Varje fältändring slänger de kvarliggande operationerna (beslut 09) — och
   * släcker röstens osäkerhetsmarkering på det fält användaren nu själv rört:
   * han HAR tittat där, det var hela poängen med markeringen.
   */
  const andra =
    <T,>(satt: (varde: T) => void, falt: VoiceDraftField | null = null) =>
    (varde: T) => {
      sparning.nollstall();
      if (falt !== null && osakra.has(falt)) {
        sattOsakra((tidigare) => {
          const kvar = new Set(tidigare);
          kvar.delete(falt);
          return kvar;
        });
      }
      satt(varde);
    };

  /**
   * UTKASTET IN I FORMULÄRET. Samma fält som handen fyller i, inget annat:
   *   - minuterna som timtext, utan att tappa en minut (timtextAv)
   *   - momenten som ikryssade rader — bara id:n som finns i ögonblicksbilden;
   *     ett moment som hann raderas mellan pull och röst blir "kryssa i själv"
   *   - ordern, om den finns här; annars ÖPPNAS VÄLJAREN. Aldrig en gissning.
   *   - dagen bara om den nämndes, milen och bilen bara om de nämndes
   *   - anteckningen: det som sades men inte passade något moment
   * Källan sätts till 'voice' så att raden bär det när den sparas.
   */
  const tillampaUtkast = (svar: VoiceTranscribeResponse): void => {
    sparning.nollstall();
    const d = svar.draft;
    sattKalla('voice');
    sattRostsvar(svar);
    sattOsakra(new Set(d.unsure));
    if (d.minutes !== null) sattTimtext(timtextAv(Number(d.minutes)));
    sattValda(d.task_ids.filter((id) => bild.moment.some((m) => m.id === id)));
    const hittad = d.work_order_id === null ? null : orderMedId(bild, d.work_order_id);
    if (hittad !== null) {
      sattOrderId(hittad.id);
      sattVisaOrderval(false);
    } else if (d.work_order_id !== null || d.unmatched.work_order !== null) {
      sattVisaOrderval(true);
    }
    if (d.work_date !== null) sattDag(d.work_date);
    if (d.meters !== null) {
      const m = Number(d.meters);
      sattMiltext(franHeltal(m, FAKTOR.mil_meter, m % 1000 === 0 ? 1 : 2));
      sattMilOppet(true);
    }
    if (d.vehicle !== null) {
      sattFordon(d.vehicle);
      sattMilOppet(true);
    }
    if (d.note !== null) sattAnteckning(d.note);
  };

  const rost = useRostinspelning(firmaId, tillampaUtkast);

  const order = orderMedId(bild, orderId);
  const krav = momentkravFor(bild, order);
  const vecka = veckanFor(dag);
  const lage = veckansLage(bild, konto.id, vecka);
  const inomFonstret = inomArbetsdagsfonstret(dag);
  const farSkriva =
    farRapportera(roll) &&
    !arLast &&
    inomFonstret &&
    farSkrivaVecka({ lage, egen: true, arChef });

  /*
    SAKNAS TIMPRISET FÖR MIN ROLL? Bara ett hårt `false` spärrar.

    `null` betyder att vyn inte bar prisdata (montör och gäst — beslut 16) och
    'laddar' att svaret inte kommit än. I båda lägena släpps sparningen fram:
    en osäker klientkontroll får aldrig låsa en app som fungerar, och servern
    har sista ordet ändå. Det enda den här raden gör är att flytta ett besked
    som redan fanns till FÖRE inmatningen i stället för efter.
  */
  const oversikt = useOversikt();
  const timprisSaknas =
    oversikt.slag === 'klar' && oversikt.oversikt.harMittTimpris === false;

  const valdaMoment: readonly TaskRow[] = useMemo(
    () => moment.filter((m) => valda.includes(m.id)),
    [moment, valda],
  );

  const minuter = timmarTillMinuter(timtext);
  const delar = rimligaMinuter(minuter)
    ? fordelaMinuter(minuter, valdaMoment.length)
    : [];
  // Varje del är en EGEN tidrad och måste klara CHECK (minutes >= 1). Fyra
  // moment på tre minuter går alltså inte att spara — och det ska det inte.
  const delarDuger = delar.length > 0 && delar.every((del) => del >= 1);

  const milmeter = miltext.trim() === '' ? null : tillHeltal(miltext, FAKTOR.mil_meter);
  const milDuger =
    miltext.trim() === '' || (milmeter !== null && milmeter >= 1 && milmeter <= MAX_METER);

  // Anteckningen är FRIVILLIG och står därför inte i kanSpara. Kravet mot
  // privatkund vilar på momentlistan, som redan är obligatorisk (regler.ts).
  const kanSpara =
    farSkriva &&
    !timprisSaknas &&
    order !== null &&
    delarDuger &&
    milDuger &&
    !sparning.sparar;

  /**
   * VARFÖR SPARA-KNAPPEN ÄR AV. En grå knapp utan besked är det sämsta
   * gränssnittet av alla — montören trycker, ingenting händer, och han vet inte
   * vad han ska göra. Ordningen följer den ordning fälten står i på skärmen.
   */
  const saknas: string | null =
    !farSkriva
      ? null // De lägena har sina egna texter längst ned.
      : timprisSaknas
        ? // FÖRST AV ALLT, för det är den enda punkten i listan som inte går att
          // rätta i det här formuläret. Chefen får vägen dit (knappen under);
          // montören får veta vem som ska göra det, för han har inte rutten.
          arChef
          ? 'Firman har inget timpris för din roll än. Utan det kan ingen tidrad frysa ett belopp.'
          : 'Firman har inget timpris för din roll än. Säg till den som driver firman — utan det stoppas raden av servern.'
      : order === null
        ? 'Välj vilket jobb tiden hör till.'
        : !rimligaMinuter(minuter)
          ? 'Skriv hur länge du jobbade.'
          : valdaMoment.length === 0
            ? 'Kryssa i minst ett moment — det är texten kunden får se.'
            : !delarDuger
              ? 'För lite tid för så många moment. Varje moment blir en egen rad och behöver minst en minut.'
              : !milDuger
                ? 'Milen ser fel ut. Högst 200 mil per rad.'
                : null;

  const byggOps = (): readonly SyncOp[] => {
    if (order === null || !rimligaMinuter(minuter) || !delarDuger) return [];
    const anteckningsvarde = anteckning.trim() === '' ? null : anteckning.trim();
    const ops: SyncOp[] = valdaMoment.map((m, i) =>
      tidradOp({
        id: nyttTidradId(),
        account_id: konto.id,
        work_date: dag,
        minutes: asMinutes(delar[i] ?? 0),
        task_id: m.id,
        work_order_id: order.id,
        note: anteckningsvarde,
        source: kalla,
        base_version: null,
      }),
    );
    // Milen åker med i SAMMA kuvert. Beslut 08: den skrivs för hand, och det är
    // här handen redan är.
    if (milmeter !== null && miltext.trim() !== '') {
      ops.push(
        milradOp({
          id: nyttMilradId(),
          account_id: konto.id,
          work_date: dag,
          meters: asMeters(milmeter),
          work_order_id: order.id,
          vehicle: fordon,
          base_version: null,
        }),
      );
    }
    return ops;
  };

  const spara = async (): Promise<void> => {
    const klart = await sparning.spara(byggOps);
    if (klart) ga('tid');
  };

  /**
   * VÄGGEN, MED EN DÖRR I.
   *
   * Texten sa åt chefen — appens hela målgrupp — att "chefen lägger upp dem",
   * och erbjöd ingen väg dit. Skärmarna 'moment' och 'ordny' finns och är nådda,
   * men bara via kugghjulet, och den som står i tidrapporten hittar dem inte.
   * Knappen visas för den som FÅR skriva stamdata (kanSeLaget = full vy); en
   * montör får kvar sin text, för han kan ändå inte lägga upp något.
   */
  if (ordrar.length === 0 || bild.moment.length === 0) {
    const saknarOrdrar = ordrar.length === 0;
    return (
      <>
        <Rubrik
          titel="Inget att rapportera på än"
          under="En tidrad kräver både en arbetsorder och ett moment."
        />
        <Tomtext>
          {saknarOrdrar
            ? 'Firman har inga arbetsordrar än. Chefen lägger upp dem under Arbetsordrar.'
            : 'Firman har inga moment än. Chefen lägger upp dem — det är de texterna som hamnar på kundens faktura.'}
        </Tomtext>
        {kanSeLaget && (
          <Knapp onClick={() => ga(saknarOrdrar ? 'ordny' : 'moment')}>
            {saknarOrdrar ? 'Lägg upp den första arbetsordern' : 'Lägg upp firmans moment'}
          </Knapp>
        )}
      </>
    );
  }

  return (
    <>
      <Rubrik
        titel={ordernamn(order)}
        under={
          <>
            {visaDag(dag)}
            {krav === 'kravs' && (
              <>
                {' · '}
                <Kravbricka>Privatkund — moment krävs</Kravbricka>
              </>
            )}
          </>
        }
      />

      <Rostknapp
        online={online}
        lyssnar={rost.lage.status === 'lyssnar'}
        disabled={!farSkriva || rost.lage.status === 'skickar'}
        onHallIn={() => {
          sparning.nollstall();
          rost.borja();
        }}
        onSlapp={rost.sluta}
      >
        {rost.lage.status === 'lyssnar'
          ? `Lyssnar … ${rost.lage.sekunder} s — släpp när du är klar`
          : rost.lage.status === 'skickar'
            ? 'Tolkar det du sa …'
            : 'Håll in och säg vad du gjort'}
      </Rostknapp>
      {!online && (
        <Tomtext>
          Rösten behöver nätet och det finns inget just nu. Klartid behöver täckning
          för att spara — vänta tills du har signal, eller skriv in tiden när du är
          tillbaka i bilen.
        </Tomtext>
      )}
      {online && rost.lage.status === 'klar' && (
        <Ikonrad ikon="rost">
          Ifyllt från det du sa: <em>"{rost.lage.svar.text}"</em> — kontrollera och
          spara.
        </Ikonrad>
      )}
      {online &&
        rost.lage.status === 'fel' &&
        (rost.lage.fel.slag === 'api' &&
        (rost.lage.fel.code === 'SERVICE_UNAVAILABLE' || rost.lage.fel.code === 'SERVER_ERROR') ? (
          <Tomtext>{rostfeltext(rost.lage.fel)}</Tomtext>
        ) : (
          <Felruta fel={rost.lage.fel} />
        ))}
      {online && rost.lage.status !== 'klar' && rost.lage.status !== 'fel' && (
        <Tomtext>{ROSTTEXT[rost.lage.status]}</Tomtext>
      )}
      {rostsvar !== null &&
        rostsvar.draft.work_order_id === null &&
        rostsvar.draft.unmatched.work_order !== null && (
          <Tomtext>
            Hittade ingen arbetsorder för <em>"{rostsvar.draft.unmatched.work_order}"</em>.
            Välj själv i listan under Jobbet — appen gissar inte.
          </Tomtext>
        )}
      {rostsvar !== null && rostsvar.draft.work_order_id !== null && order === null && (
        <Tomtext>
          Arbetsordern rösten hörde finns inte längre i listan. Välj själv under
          Jobbet.
        </Tomtext>
      )}
      {rostsvar !== null && rostsvar.draft.unmatched.tasks.length > 0 && (
        <Tomtext>
          <em>"{rostsvar.draft.unmatched.tasks.join(', ')}"</em> passade inget moment i
          firmans lista — det ligger som förslag i anteckningen i stället.
        </Tomtext>
      )}

      <Snabbrad>
        <Knapp variant="snabb" onClick={() => andra(sattTimtext, 'minutes')('4')}>
          4 h
        </Knapp>
        <Knapp variant="snabb" onClick={() => andra(sattTimtext, 'minutes')('6')}>
          6 h
        </Knapp>
        <Knapp variant="snabb" onClick={() => andra(sattTimtext, 'minutes')('8')}>
          8 h
        </Knapp>
      </Snabbrad>

      <Talfalt
        etikett="Hur länge?"
        varde={timtext}
        onAndra={(text) => andra(sattTimtext, 'minutes')(text)}
        faktor={FAKTOR.timmar_minuter}
        suffix="h"
        placeholder="8"
        hjalp="7,5 eller 7:30 går lika bra."
        varning={
          osakra.has('minutes')
            ? 'Rösten var osäker på tiden — kolla. Sa du ett klockslag utan rast är rasten inte avdragen.'
            : undefined
        }
        fel={
          timtext.trim() !== '' && !rimligaMinuter(minuter)
            ? `Skriv tiden som 7,5 eller 7:30, högst ${MAX_MINUTER / 60} timmar.`
            : undefined
        }
      />

      <Yta
        rubrik="Vad gjorde du?"
        rakning={rimligaMinuter(minuter) ? visaMinuter(minuter) : '0,0 h'}
      >
        {moment.map((m) => {
          const plats = valdaMoment.findIndex((valt) => valt.id === m.id);
          const del = plats >= 0 ? delar[plats] : undefined;
          return (
            <Kryssrad
              key={m.id}
              ikryssad={plats >= 0}
              onToggle={() =>
                andra(sattValda, 'task_ids')(
                  valda.includes(m.id)
                    ? valda.filter((id) => id !== m.id)
                    : [...valda, m.id],
                )
              }
              varde={del === undefined ? undefined : visaMinuter(del)}
            >
              {m.name}
            </Kryssrad>
          );
        })}
        {valdaMoment.length === 0 && (
          <Tomtext>Kryssa i vad du gjorde. Flera moment delar timmarna lika.</Tomtext>
        )}
        {osakra.has('task_ids') && (
          <Tomtext>
            <Bricka ton="tilldelad">Kolla</Bricka> Rösten var osäker på momenten —
            se att rätt rader är ikryssade.
          </Tomtext>
        )}
      </Yta>

      <Textyta
        etikett="Anteckning"
        varde={anteckning}
        onAndra={(text) => andra(sattAnteckning)(text)}
        rows={2}
        maxLength={ANTECKNING_MAX_TECKEN}
        placeholder="Bytte utkastare och provtryckte"
        hjalp={
          krav === 'kravs'
            ? 'Frivillig. Kunden läser momenten, inte den här.'
            : 'Frivillig. Följer med underlaget.'
        }
      />

      <Installningsrad
        ikon="ordrar"
        etikett="Jobbet"
        varde={
          osakra.has('work_order_id') ? (
            <>
              {ordernamn(order)} <Bricka ton="tilldelad">Kolla</Bricka>
            </>
          ) : (
            ordernamn(order)
          )
        }
        atgard={
          <Knapp variant="piller-mini" onClick={() => sattVisaOrderval(!visaOrderval)}>
            Byt
          </Knapp>
        }
      />
      {visaOrderval && (
        <Valfalt
          etikett="Arbetsorder"
          varde={orderId ?? ''}
          onAndra={(id) => {
            andra(sattOrderId, 'work_order_id')(id);
            sattVisaOrderval(false);
          }}
          val={ordrar.map((o) => ({
            varde: String(o.id),
            etikett:
              orderunder(o) === undefined
                ? ordernamn(o)
                : `${ordernamn(o)} — ${orderunder(o) ?? ''}`,
          }))}
          hjalp="Listan är de ordrar du får rapportera på."
        />
      )}

      <Installningsrad
        ikon="tid"
        etikett="Dagen"
        varde={
          osakra.has('work_date') ? (
            <>
              {visaDag(dag)} <Bricka ton="tilldelad">Kolla</Bricka>
            </>
          ) : (
            visaDag(dag)
          )
        }
        atgard={
          <Knapp variant="piller-mini" onClick={() => sattVisaDagval(!visaDagval)}>
            Ändra
          </Knapp>
        }
      />
      {visaDagval && (
        <Falt
          etikett="Arbetsdag"
          type="date"
          varde={dag}
          min={tidigastArbetsdag()}
          max={senastArbetsdag()}
          onAndra={(text) => {
            // <input type="date"> lämnar tom sträng när fältet rensas, och
            // formen prövas i arbetsdagUrFalt — aldrig ett bart `as WorkDate`.
            const vald = arbetsdagUrFalt(text);
            if (vald === null) return;
            andra(sattDag, 'work_date')(vald);
          }}
          hjalp="Svensk tid, inte telefonens tidszon."
        />
      )}

      <Installningsrad
        ikon="mil"
        etikett="Körde du något?"
        varde={
          milmeter !== null && miltext.trim() !== '' ? visaMil(milmeter) : 'Inget'
        }
        atgard={
          <Knapp variant="piller-mini" onClick={() => sattMilOppet(!milOppet)}>
            {milOppet ? 'Klar' : 'Ändra'}
          </Knapp>
        }
      />
      {milOppet && (
        <>
          <Talfalt
            etikett="Mil till och från jobbet"
            varde={miltext}
            onAndra={(text) => andra(sattMiltext, 'meters')(text)}
            faktor={FAKTOR.mil_meter}
            suffix="mil"
            placeholder="4,5"
            hjalp="Skrivs för hand. Ingen spårar var du är."
            varning={osakra.has('meters') ? 'Rösten var osäker på milen — kolla.' : undefined}
            fel={milDuger ? undefined : 'Högst 200 mil per rad.'}
          />
          <Valfalt
            etikett="Bil"
            varde={fordon}
            onAndra={(v) => andra(sattFordon, 'vehicle')(v)}
            val={FORDON.map((f) => ({ varde: f, etikett: FORDONSTEXT[f] }))}
            hjalp={osakra.has('vehicle') ? 'Rösten var osäker på bilen — kolla.' : undefined}
          />
        </>
      )}

      <Knapp variant="stor" disabled={!kanSpara} onClick={() => void spara()}>
        {sparning.sparar
          ? 'Sparar ...'
          : `Spara ${rimligaMinuter(minuter) ? visaMinuter(minuter) : '0,0 h'}`}
      </Knapp>
      {saknas !== null && <Tomtext>{saknas}</Tomtext>}

      {/* VÄGEN DIT, inte bara beskedet. Ett avslag utan väg vidare är det som
          gjorde PRICE_MISSING så dyrt: man visste vad som fattades och inte var
          man satte det. Knappen finns bara för chefen — han är den enda som har
          rutten (put:price_rate är MANAGER_ONLY). */}
      {timprisSaknas && arChef && (
        <Knapp variant="stor-tyst" onClick={() => ga('timpriser')}>
          Sätt timpriset
        </Knapp>
      )}

      {sparning.fel !== null && (
        <Felruta
          fel={sparning.fel}
          atgard={
            <Knapp
              variant="piller-mini"
              disabled={sparning.sparar}
              onClick={() => void spara()}
            >
              Försök igen
            </Knapp>
          }
        />
      )}
      {sparning.fallna.map((rad) => (
        <Felruta key={rad.op_id} fel={felAvOperation(rad)} />
      ))}

      {!inomFonstret && (
        <Tomtext>
          Dagen ligger utanför det fönster som går att rapportera i. Välj en dag
          mellan {tidigastArbetsdag()} och {senastArbetsdag()}.
        </Tomtext>
      )}
      {!farSkrivaVecka({ lage, egen: true, arChef }) && (
        <Tomtext>
          Veckan är {VECKOLAGETEXT[lage].toLowerCase()} och kan inte ändras. Be chefen
          öppna den igen om något ska rättas.
        </Tomtext>
      )}
      {arLast && (
        <Tomtext>
          Prenumerationen är pausad. Allt går att läsa och exportera, men inget nytt
          kan sparas förrän betalningen är ordnad.
        </Tomtext>
      )}

      <Tomtext>Momenten hamnar ordagrant på kundens faktura. Timpriset syns inte här.</Tomtext>
    </>
  );
};
